import { z } from "zod";

import { jurisdictionProfileSchema } from "./cableContract";
import {
  ALWAYS_ROUTE_INFORMATION_CODES,
  allowanceBasisSchema,
  cableConstructionSchema,
  cableRowSourceSchema,
  cableSizingStatusSchema,
  cableTrayStatusSchema,
  cableTrayWarningCodeSchema,
  conductorArrangementSchema,
  conductorMaterialSchema,
  coverKindSchema,
  fillBasisSchema,
  installationMethodSchema,
  insulationMaterialSchema,
  layerModeSchema,
  loadBasisSchema,
  ratedLoadBasisSchema,
  trayArrangementSchema,
  trayTypeSchema,
} from "./cableTrayContract";
import { calculationRunSummarySchema } from "./calculationRun";
import { CHOOSE_A_CABLE_RUN, type CableRunChoice, listCableRunsForRevision } from "./earthFaultLoop";
import { exactDecimalSchema } from "./faultContract";
import { ApiError, describeApiFieldError } from "./http";

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const optionalSource = (maxLength = 300) => z.string().trim().min(1).max(maxLength).optional();

const RUNS_URL = "/api/v1/electrical/cable-tray/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The cable-run selector (list and sentence) is the one the earth-fault loop page uses; a tray
// row names a stored EOS-06 run of the revision in the same way (design note section 2).
export { CHOOSE_A_CABLE_RUN, listCableRunsForRevision };
export type { CableRunChoice };

// The choices the route needs; the backend imposes no default on them.
export const REQUIRED_ROUTE_CHOICES = ["tray_type", "arrangement"] as const;

// A cable typed by the engineer when no stored run exists, with its source.
export const declaredCableRequestSchema = z
  .object({
    construction: cableConstructionSchema.optional(),
    cores: z.number().int().min(1).max(61),
    conductor_material: conductorMaterialSchema.optional(),
    insulation_material: insulationMaterialSchema.optional(),
    source: optionalSource(),
  })
  .strict()
  .superRefine((cable, ctx) => {
    for (const field of ["construction", "conductor_material", "insulation_material"] as const) {
      if (cable[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
  });

export const fillLimitRequestSchema = z
  .object({ percent: exactDecimalSchema, source: optionalSource() })
  .strict();

export const ratedLoadRequestSchema = z
  .object({
    value_kg: exactDecimalSchema,
    basis: ratedLoadBasisSchema.optional(),
    rated_span_m: exactDecimalSchema.optional(),
    source: optionalSource(),
  })
  .strict()
  .superRefine((rated, ctx) => {
    if (rated.basis === "PER_SPAN" && rated.rated_span_m === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "A rated load stated per span needs the rated span it applies to.",
        path: ["rated_span_m"],
      });
    }
  });

export const supportSpacingRequestSchema = z
  .object({ value_m: exactDecimalSchema, source: optionalSource() })
  .strict();

export const futureAllowanceRequestSchema = z
  .object({
    percent: exactDecimalSchema,
    source: optionalSource(),
    basis: allowanceBasisSchema.optional(),
  })
  .strict();

// One cable (or one parallel set) on the route: its cable from exactly one place - a stored
// EOS-06 run of the revision (the quantity defaults to its parallel runs), or the typed cable
// (the quantity is then required). The diameter and the mass are declared here with their
// sources; a blank item is left out: NOT CHECKED, never assumed.
export const cableRowRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    cable_run_id: z.string().optional(),
    cable: declaredCableRequestSchema.optional(),
    quantity: z.number().int().min(1).max(500).optional(),
    quantity_source: optionalSource(),
    overall_diameter_mm: exactDecimalSchema.optional(),
    diameter_source: optionalSource(),
    mass_kg_per_m: exactDecimalSchema.optional(),
    mass_source: optionalSource(),
    spacing_mm: exactDecimalSchema.optional(),
    spacing_in_diameters: exactDecimalSchema.optional(),
    spacing_source: optionalSource(),
    sld_element_code: z.string().trim().min(1).max(50).optional(),
  })
  .strict()
  .superRefine((row, ctx) => {
    if ((row.cable_run_id === undefined) === (row.cable === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "choose one cable source: a linked cable run, or the typed cable",
        path: ["cable_run_id"],
      });
    } else if (row.cable_run_id !== undefined && !UUID.test(row.cable_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_CABLE_RUN, path: ["cable_run_id"] });
    }
    if (row.cable !== undefined && row.quantity === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "a typed cable needs the number of cables",
        path: ["quantity"],
      });
    }
    if (row.spacing_mm !== undefined && row.spacing_in_diameters !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "declare the spacing in millimetres or in diameters, not both",
        path: ["spacing_in_diameters"],
      });
    }
  });

export const cableTrayRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    tray_type: trayTypeSchema.optional(),
    arrangement: trayArrangementSchema.optional(),
    usable_width_mm: exactDecimalSchema,
    usable_depth_mm: exactDecimalSchema,
    dimensions_source: optionalSource(),
    cover: coverKindSchema.optional(),
    fill_limit: fillLimitRequestSchema.optional(),
    rated_load: ratedLoadRequestSchema.optional(),
    support_spacing: supportSpacingRequestSchema.optional(),
    future_allowance: futureAllowanceRequestSchema.optional(),
    rows: z.array(cableRowRequestSchema).min(1).max(200),
    sld_element_codes: z.array(z.string().trim().min(1).max(50)).max(50).optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    for (const field of REQUIRED_ROUTE_CHOICES) {
      if (values[field] === undefined) {
        ctx.addIssue({
          code: "invalid_type",
          expected: "string",
          received: "undefined",
          message: `${field} is required`,
          path: [field],
        });
      }
    }
    const codes = values.rows.map((row) => row.code);
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({
        code: "custom",
        message: "cable row codes must be unique within a study",
        path: ["rows"],
      });
    }
    const feeders = values.sld_element_codes ?? [];
    if (new Set(feeders).size !== feeders.length) {
      ctx.addIssue({
        code: "custom",
        message: "sld_element_codes must be unique",
        path: ["sld_element_codes"],
      });
    }
  });

export const cableTrayWarningSchema = z
  .object({ code: cableTrayWarningCodeSchema, message: z.string() })
  .strict();

export const linkedCableRunResponseSchema = z
  .object({
    cable_run_id: z.string().uuid(),
    study_code: z.string(),
    revision_number: z.number().int(),
    run_status: cableSizingStatusSchema,
    construction: cableConstructionSchema,
    conductor_material: conductorMaterialSchema,
    insulation_material: insulationMaterialSchema,
    installation_method: installationMethodSchema,
    arrangement: conductorArrangementSchema,
    parallel_runs: z.number().int(),
    grouped_circuits: z.number().int().nullable(),
    phase_area_mm2: z.string().nullable(),
    number_of_loaded_conductors: z.number().int().nullable(),
  })
  .strict();

export const declaredCableResponseSchema = z
  .object({
    construction: cableConstructionSchema,
    cores: z.number().int(),
    conductor_material: conductorMaterialSchema,
    insulation_material: insulationMaterialSchema,
    source: z.string().nullable(),
  })
  .strict();

export const fillLimitResponseSchema = z
  .object({ percent: z.string(), source: z.string().nullable() })
  .strict();

export const ratedLoadResponseSchema = z
  .object({
    value_kg: z.string(),
    basis: ratedLoadBasisSchema,
    rated_span_m: z.string().nullable(),
    source: z.string().nullable(),
  })
  .strict();

export const supportSpacingResponseSchema = z
  .object({ value_m: z.string(), source: z.string().nullable() })
  .strict();

export const futureAllowanceResponseSchema = z
  .object({ percent: z.string(), source: z.string().nullable(), basis: allowanceBasisSchema })
  .strict();

// One row as checked: every input echoed, its figures formed exactly when their inputs exist
// (null otherwise; F1: a field added later is optional on this side).
export const cableRowResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    cable_source: cableRowSourceSchema,
    linked_cable_run: linkedCableRunResponseSchema.nullable(),
    declared_cable: declaredCableResponseSchema.nullable(),
    quantity: z.number().int(),
    quantity_source: z.string().nullable(),
    overall_diameter_mm: z.string().nullable(),
    diameter_source: z.string().nullable(),
    mass_kg_per_m: z.string().nullable(),
    mass_source: z.string().nullable(),
    spacing_mm: z.string().nullable(),
    spacing_source: z.string().nullable(),
    occupied_area_mm2: z.string().nullable(),
    occupied_width_mm: z.string().nullable(),
    load_kg_per_m: z.string().nullable(),
    sld_element_code: z.string().nullable(),
    status: cableTrayStatusSchema,
    warnings: z.array(cableTrayWarningSchema),
  })
  .strict();

export const cableTrayResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    tray_type: trayTypeSchema,
    arrangement: trayArrangementSchema,
    layer_mode: layerModeSchema,
    usable_width_mm: z.string(),
    usable_depth_mm: z.string(),
    dimensions_source: z.string().nullable(),
    cover: coverKindSchema,
    usable_area_mm2: z.string(),
    total_cables: z.number().int(),
    fill_basis: fillBasisSchema,
    occupied_area_mm2: z.string().nullable(),
    fill_ratio: z.string().nullable(),
    fill_percent: z.string().nullable(),
    fill_percent_with_allowance: z.string().nullable(),
    fill_limit: fillLimitResponseSchema.nullable(),
    fill_margin_percent: z.string().nullable(),
    fill_met: z.boolean().nullable(),
    occupied_width_mm: z.string().nullable(),
    occupied_width_with_allowance_mm: z.string().nullable(),
    remaining_width_mm: z.string().nullable(),
    width_ratio: z.string().nullable(),
    width_met: z.boolean().nullable(),
    largest_diameter_mm: z.string().nullable(),
    depth_met: z.boolean().nullable(),
    load_basis: loadBasisSchema,
    mass_kg_per_m: z.string().nullable(),
    future_allowance: futureAllowanceResponseSchema.nullable(),
    load_kg_per_m: z.string().nullable(),
    rated_load: ratedLoadResponseSchema.nullable(),
    rated_load_kg_per_m: z.string().nullable(),
    load_ratio: z.string().nullable(),
    load_margin_kg_per_m: z.string().nullable(),
    load_met: z.boolean().nullable(),
    support_spacing: supportSpacingResponseSchema.nullable(),
    span_covered: z.boolean().nullable(),
    sld_element_codes: z.array(z.string()),
    jurisdiction_profile: jurisdictionProfileSchema,
    rows: z.array(cableRowResponseSchema),
    status: cableTrayStatusSchema,
    warnings: z.array(cableTrayWarningSchema),
    notes: z.string().nullable(),
  })
  .strict();

export const cableTrayRunCreateRequestSchema = z
  .object({
    study: cableTrayRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const cableTrayRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: cableTrayResponseSchema,
  })
  .strict();

export type DeclaredCableRequest = z.infer<typeof declaredCableRequestSchema>;
export type CableRowRequest = z.infer<typeof cableRowRequestSchema>;
export type CableTrayRequest = z.infer<typeof cableTrayRequestSchema>;
export type CableTrayWarning = z.infer<typeof cableTrayWarningSchema>;
export type LinkedCableRunResponse = z.infer<typeof linkedCableRunResponseSchema>;
export type CableRowResponse = z.infer<typeof cableRowResponseSchema>;
export type CableTrayResponse = z.infer<typeof cableTrayResponseSchema>;
export type CableTrayRunCreateRequest = z.infer<typeof cableTrayRunCreateRequestSchema>;
export type CableTrayRunResponse = z.infer<typeof cableTrayRunResponseSchema>;

export { ALWAYS_ROUTE_INFORMATION_CODES };

function formatApiError(data: unknown, status: number, subject: string): string {
  return describeApiFieldError(data, status, `${subject} failed`);
}

function requestSignal(signal?: AbortSignal): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(30_000);
  return signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
}

/**
 * Calculate a cable tray study and persist it as a run revision.
 *
 * With a project revision id the run is stored under that revision of the selected project -
 * and a linked cable run must then belong to the same revision; without one it belongs to no
 * project. An unchanged study returns the stored revision with HTTP 200 instead of a new one
 * (A11); both are valid here.
 */
export async function createCableTrayRun(
  payload: CableTrayRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<CableTrayRunResponse> {
  const validatedPayload = cableTrayRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(formatApiError(data, response.status, "Cable tray"), response.status);
  }

  const parsed = cableTrayRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error("Unexpected response from the KES Electrical OS cable tray runs API.");
  }

  return parsed.data;
}
